import React, { useState, useEffect } from 'react';
import { Modal, Button, message, Table, Alert } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { t } from '../../../../i18n';
import ImageUploader from '../../../../components/ImageUploader';
import { batchUploadDriverPhotos, DriverPhotosInput } from '../graphql/mutations/batchUploadDriverPhotos';
import { getAccountDeliveryDriversByIds } from '../graphql/queries/getAccountDeliveryDriversByIds';
import { AccountDeliveryDriverType } from '../types/AccountDeliveryDriverType';
import { ColumnsType } from 'antd/es/table';

interface DriverPhotoUploadItem {
    accountId: number;
    phone: string;
    name: string;
    idDocumentType: number | null;
    idDocumentNumber: string | null;
    vehiclePlate: string | null;
    idDocumentImageS3Keys: string[];
    vehicleImageS3Keys: string[];
}

interface BatchUploadDriverPhotosModalProps {
    visible: boolean;
    accountIds: number[];
    onSuccess: () => void;
    onCancel: () => void;
}

const BatchUploadDriverPhotosModal: React.FC<BatchUploadDriverPhotosModalProps> = ({
    visible,
    accountIds,
    onSuccess,
    onCancel
}) => {
    const [loading, setLoading] = useState(false);
    const [uploadItems, setUploadItems] = useState<DriverPhotoUploadItem[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [skipConfirmVisible, setSkipConfirmVisible] = useState(false);

    // 加载司机信息
    useEffect(() => {
        if (visible && accountIds.length > 0) {
            loadDriverInfo();
        } else {
            setUploadItems([]);
        }
    }, [visible, accountIds]);

    const loadDriverInfo = async () => {
        setLoading(true);
        try {
            const drivers = await getAccountDeliveryDriversByIds(accountIds);
            const items: DriverPhotoUploadItem[] = drivers.map(driver => ({
                accountId: driver.accountId,
                phone: driver.account?.phone || '',
                name: driver.account?.name || '',
                idDocumentType: driver.idDocumentType ?? null,
                idDocumentNumber: driver.idDocumentNumber || null,
                vehiclePlate: driver.vehiclePlate || null,
                idDocumentImageS3Keys: driver.idDocumentImageS3KeysJson || [],
                vehicleImageS3Keys: driver.vehicleImageS3KeysJson || []
            }));
            setUploadItems(items);
        } catch (error) {
            message.error(t('delivery.driver.loadDriverInfoFailed'));
            console.error('Load driver info error:', error);
        } finally {
            setLoading(false);
        }
    };

    // 更新证件照片
    const updateIdDocumentPhotos = (accountId: number, s3Keys: string[]) => {
        setUploadItems(prev => prev.map(item => 
            item.accountId === accountId 
                ? { ...item, idDocumentImageS3Keys: s3Keys }
                : item
        ));
    };

    // 更新车牌照片
    const updateVehiclePhotos = (accountId: number, s3Keys: string[]) => {
        setUploadItems(prev => prev.map(item => 
            item.accountId === accountId 
                ? { ...item, vehicleImageS3Keys: s3Keys }
                : item
        ));
    };

    // 获取证件类型显示文本
    const getIdDocumentTypeText = (type: number | null): string => {
        if (type === null) return '-';
        switch (type) {
            case 0: return t('delivery.driver.driverLicense');
            case 1: return t('delivery.driver.idCard');
            case 2: return t('delivery.driver.passport');
            case 3: return t('delivery.driver.ead');
            default: return '-';
        }
    };

    // 表格列定义
    const columns: ColumnsType<DriverPhotoUploadItem> = [
        {
            title: t('delivery.driver.phoneNumber'),
            dataIndex: 'phone',
            key: 'phone',
            width: 150,
        },
        {
            title: t('delivery.driver.name'),
            dataIndex: 'name',
            key: 'name',
            width: 120,
        },
        {
            title: t('delivery.driver.idDocumentType'),
            dataIndex: 'idDocumentType',
            key: 'idDocumentType',
            width: 120,
            render: (type: number | null) => getIdDocumentTypeText(type),
        },
        {
            title: t('delivery.driver.idDocumentNumber'),
            dataIndex: 'idDocumentNumber',
            key: 'idDocumentNumber',
            width: 150,
        },
        {
            title: t('delivery.driver.vehiclePlate'),
            dataIndex: 'vehiclePlate',
            key: 'vehiclePlate',
            width: 120,
        },
        {
            title: t('delivery.driver.idDocumentPhotos'),
            key: 'idDocumentPhotos',
            className: 'id-document-photos-column',
            render: (_: any, record: DriverPhotoUploadItem) => (
                <div className="id-document-photos-wrapper">
                    <ImageUploader
                        value={record.idDocumentImageS3Keys}
                        onChange={(s3Keys: string[]) => updateIdDocumentPhotos(record.accountId, s3Keys)}
                        maxCount={6}
                    />
                </div>
            ),
        },
        {
            title: t('delivery.driver.vehiclePhotos'),
            key: 'vehiclePhotos',
            width: 200,
            render: (_: any, record: DriverPhotoUploadItem) => (
                <ImageUploader
                    value={record.vehicleImageS3Keys}
                    onChange={(s3Keys: string[]) => updateVehiclePhotos(record.accountId, s3Keys)}
                    maxCount={1}
                />
            ),
        },
    ];

    // 跳过处理
    const handleSkip = () => {
        setSkipConfirmVisible(true);
    };

    // 确认跳过
    const handleSkipConfirm = () => {
        setSkipConfirmVisible(false);
        onCancel();
    };

    // 确认上传
    const handleConfirm = async () => {
        // 筛选出已上传照片的司机（证件照片和车辆照片都必须有）
        const completeItems = uploadItems.filter(item => 
            item.idDocumentImageS3Keys.length > 0 && item.vehicleImageS3Keys.length > 0
        );

        // 检查是否有已上传照片的司机
        if (completeItems.length === 0) {
            message.warning(t('delivery.driver.noPhotosToUpload'));
            return;
        }

        // 如果有部分司机未上传照片，提示但不阻止提交
        const incompleteItems = uploadItems.filter(item => 
            item.idDocumentImageS3Keys.length === 0 || item.vehicleImageS3Keys.length === 0
        );
        if (incompleteItems.length > 0) {
            message.info(t('delivery.driver.photosIncomplete', { count: incompleteItems.length }));
        }

        setSubmitting(true);
        try {
            // 只构建已上传照片的司机的上传数据
            const photosMap = new Map<number, DriverPhotosInput>();
            const accountIdsToUpload: number[] = [];
            completeItems.forEach(item => {
                photosMap.set(item.accountId, {
                    idDocumentImageS3Keys: item.idDocumentImageS3Keys,
                    vehicleImageS3Keys: item.vehicleImageS3Keys
                });
                accountIdsToUpload.push(item.accountId);
            });

            // 调用批量上传接口，只上传已上传照片的司机
            const result = await batchUploadDriverPhotos(accountIdsToUpload, photosMap);
            
            // 检查上传结果
            const failedResults = result.results.filter(r => !r.success);
            if (failedResults.length > 0) {
                message.warning(t('delivery.driver.uploadPartiallyFailed', { count: failedResults.length }));
            } else {
                message.success(t('delivery.driver.uploadSuccess', { count: completeItems.length }));
            }

            onSuccess();
        } catch (error) {
            message.error(t('delivery.driver.uploadFailed'));
            console.error('Upload photos error:', error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Modal
                title={
                    <div className="flex items-center">
                        <div className="h-6 w-1 bg-purple-600 mr-3"></div>
                        <span className="text-xl font-medium">{t('delivery.driver.batchUploadPhotosTitle')}</span>
                    </div>
                }
                open={visible}
                onCancel={onCancel}
                width={1200}
                maskClosable={false}
                footer={[
                    <Button key="skip" onClick={handleSkip}>
                        {t('delivery.driver.skip')}
                    </Button>,
                    <Button key="confirm" type="primary" loading={submitting} onClick={handleConfirm}>
                        {t('delivery.driver.confirm')}
                    </Button>,
                ]}
            >
                {/* 暂时隐藏提示信息，后续有需要再放开 */}
                {/* <Alert
                    message={t('delivery.driver.uploadPhotosWarning')}
                    type="warning"
                    showIcon
                    icon={<ExclamationCircleOutlined />}
                    style={{ marginBottom: 16 }}
                /> */}
                
                <style>{`
                    /* 确保图片在同一行展示 */
                    .id-document-photos-wrapper .ant-upload-list {
                        display: flex !important;
                        flex-wrap: nowrap !important;
                        gap: 8px;
                    }
                    
                    /* 控制每个图片项的尺寸 */
                    .id-document-photos-wrapper .ant-upload-list-item {
                        margin: 0 !important;
                        width: 96px !important;
                        height: 96px !important;
                        flex-shrink: 0;
                    }
                    
                    /* 控制上传按钮的尺寸 */
                    .id-document-photos-wrapper .ant-upload-select {
                        width: 96px !important;
                        height: 96px !important;
                        margin: 0 !important;
                        flex-shrink: 0;
                    }
                    
                    /* 让列宽根据内容自适应 */
                    .id-document-photos-column {
                        min-width: 104px;
                    }
                `}</style>
                <Table
                    columns={columns}
                    dataSource={uploadItems}
                    rowKey="accountId"
                    loading={loading}
                    pagination={false}
                    scroll={{ y: 400, x: 'max-content' }}
                    bordered
                 className="mb-4"/>
            </Modal>
            <Modal
                title={
                    <div className="flex items-center">
                        <div className="h-6 w-1 bg-purple-600 mr-3"></div>
                        <span className="text-xl font-medium">{t('delivery.driver.skipConfirm')}</span>
                    </div>
                }
                open={skipConfirmVisible}
                onCancel={() => setSkipConfirmVisible(false)}
                width={500}
                maskClosable={false}
                footer={[
                    <Button key="cancel" onClick={() => setSkipConfirmVisible(false)}>
                        {t('components.button.cancel')}
                    </Button>,
                    <Button key="confirm" type="primary" onClick={handleSkipConfirm}>
                        {t('components.button.confirm')}
                    </Button>,
                ]}
            >
                <div className="flex items-start mb-4">
                    <ExclamationCircleOutlined className="text-yellow-500 text-xl mr-3 mt-1" />
                    <div className="flex-1">
                        {t('delivery.driver.skipConfirmContent')}
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default BatchUploadDriverPhotosModal;

